import { Link } from "react-router-dom";
import type { CategoryHistoryItem, DepartmentAssignment, MembershipCategory } from "../../api/members";

interface MemberTrajectoryProps {
  categoryHistory: CategoryHistoryItem[];
  departmentAssignments: DepartmentAssignment[];
  effectiveSince: string | null;
  memberId: string;
  canWriteDepartments: boolean;
}

interface TrajectoryEvent {
  key: string;
  date: string;
  title: string;
  detail?: string;
  editLink?: string;
}

function categoryLabel(category: MembershipCategory): string {
  return category === "efetivo" ? "Efetivo" : "Colaborador";
}

function formatDate(value: string, includeTime = false): string {
  const date = new Date(value.includes("T") ? value : `${value}T12:00:00`);
  return new Intl.DateTimeFormat("pt-BR", includeTime ? { dateStyle: "medium", timeStyle: "short" } : { dateStyle: "medium" }).format(date);
}

/**
 * Une, em uma única linha do tempo, os eventos que antes ficavam espalhados entre "Departamentos
 * e atividades" e "Histórico de categoria" (Fase 3). Todo evento corresponde a um dado real dos
 * contratos de leitura existentes — nada é interpolado ou inventado.
 */
function buildEvents(
  categoryHistory: CategoryHistoryItem[],
  departmentAssignments: DepartmentAssignment[],
  effectiveSince: string | null,
  memberId: string,
  canWriteDepartments: boolean,
): TrajectoryEvent[] {
  const events: TrajectoryEvent[] = [];

  if (effectiveSince) {
    events.push({ key: "effective-since", date: effectiveSince, title: "Efetivação institucional" });
  }

  categoryHistory.forEach((item, index) => {
    events.push({
      key: `category-${index}-${item.changed_at}`,
      date: item.changed_at,
      title: `${categoryLabel(item.previous_category)} para ${categoryLabel(item.new_category)}`,
      detail: item.justification,
    });
  });

  departmentAssignments.forEach((assignment) => {
    const stillActive = !assignment.ended_at;
    events.push({
      key: `dept-start-${assignment.id}`,
      date: assignment.started_at,
      title: `Início em ${assignment.department}${stillActive ? " (atual)" : ""}`,
      detail: [assignment.activity, assignment.role].filter(Boolean).join(" · ") || undefined,
      editLink: canWriteDepartments ? `/associados/${memberId}/departamentos/${assignment.id}` : undefined,
    });
    if (assignment.ended_at) {
      events.push({
        key: `dept-end-${assignment.id}`,
        date: assignment.ended_at,
        title: `Saída de ${assignment.department}`,
      });
    }
  });

  return events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function MemberTrajectory({ categoryHistory, departmentAssignments, effectiveSince, memberId, canWriteDepartments }: MemberTrajectoryProps) {
  const events = buildEvents(categoryHistory, departmentAssignments, effectiveSince, memberId, canWriteDepartments);

  if (events.length === 0) {
    return <p className="member-profile-section__empty">Ainda não há registro histórico para este associado.</p>;
  }

  return (
    <ol className="member-timeline">
      {events.map((event) => (
        <li key={event.key}>
          <time dateTime={event.date}>{formatDate(event.date)}</time>
          <p>
            <strong>{event.title}</strong>
          </p>
          {event.detail && <span>{event.detail}</span>}
          {event.editLink && <Link to={event.editLink}>Editar vínculo</Link>}
        </li>
      ))}
    </ol>
  );
}
